//src/page/admin/AdminDashboard.js - FINAL SIMPLIFIED (3 Stats Only)

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getStoreDashboardStats, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { useStoreSettings } from "../../../hooks/useStoreSettings";
import { getLocalizedProductName } from "../../../utils/multilingualUtils";
import "./AdminDashboard.css";

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalSales: 0,
        totalOrders: 0,
        averageOrderValue: 0
    });
    const [recentOrders, setRecentOrders] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Get store settings for currency and store name
    const { currency: storeCurrency, storeSettings } = useStoreSettings(DEFAULT_STORE_ID);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const dashboardData = await getStoreDashboardStats(DEFAULT_STORE_ID);

                // Update state with the fetched data
                setStats({
                    totalSales: dashboardData.totalSales || 0,
                    totalOrders: dashboardData.totalOrders || 0,
                    averageOrderValue: dashboardData.averageOrderValue || 0
                });

                setRecentOrders(dashboardData.recentOrders || []);
                setTopProducts(dashboardData.topProducts || []);

            } catch (error) {
                console.error("Error fetching dashboard data:", error);
                error.message = "Failed to load dashboard data. Please try again later.";

            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Format date for display
    const formatDate = (date) => {
        if (!date) return "";

        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const StatusBadge = ({ status }) => {
        const getStatusClass = () => {
            switch (status?.toLowerCase()) {
                case 'delivered':
                    return 'status-delivered';
                case 'processing':
                    return 'status-processing';
                case 'pending':
                    return 'status-pending';
                case 'cancelled':
                    return 'status-cancelled';
                default:
                    return '';
            }
        };

        return <span className={`status-badge ${getStatusClass()}`}>{status}</span>;
    };

    // Handle add product - limits enforced server-side
    const handleAddProduct = () => {
        navigate("/admin/products/new");
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <p>Loading dashboard data...</p>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            {/* Store Name Header */}
            <div className="admin-dashboard-header">
                <h1>{storeSettings?.name || "Your Store"} Dashboard</h1>
                <p>Welcome back! Here's an overview of your store performance.</p>
            </div>

            {/* Stats Cards - SIMPLIFIED to 3 cards only */}
            <div className="admin-stats-grid">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon sales-icon">💰</div>
                    <div className="admin-stat-content">
                        <h3 className="admin-stat-title">Total Sales</h3>
                        <p className="admin-stat-value">{storeCurrency} {stats.totalSales.toFixed(2)}</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon orders-icon">📦</div>
                    <div className="admin-stat-content">
                        <h3 className="admin-stat-title">Total Orders</h3>
                        <p className="admin-stat-value">{stats.totalOrders}</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon avg-icon">📊</div>
                    <div className="admin-stat-content">
                        <h3 className="admin-stat-title">Avg. Order Value</h3>
                        <p className="admin-stat-value">{storeCurrency} {stats.averageOrderValue.toFixed(2)}</p>
                    </div>
                </div>
            </div>

            {/* Recent Orders */}
            <div className="admin-dashboard-section">
                <div className="admin-section-header">
                    <h2>Recent Orders</h2>
                    <Link to="/admin/orders" className="admin-view-all">View All</Link>
                </div>

                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Order ID</th>
                                <th>Customer</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Total</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentOrders.length > 0 ? (
                                recentOrders.map(order => (
                                    <tr key={order.id}>
                                        <td>{order.id}</td>
                                        <td>{order.customerInfo?.firstName} {order.customerInfo?.lastName}</td>
                                        <td>{formatDate(order.createdAt)}</td>
                                        <td><StatusBadge status={order.status} /></td>
                                        <td>{storeCurrency} {order.total?.toFixed(2)}</td>
                                        <td>
                                            <Link to={`/admin/orders/${order.id}`} className="admin-action-btn">View</Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="no-data">No recent orders found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Top Products */}
            <div className="admin-dashboard-section">
                <div className="admin-section-header">
                    <h2>Top Selling Products</h2>
                    <Link to="/admin/products" className="admin-view-all">View All Products</Link>
                </div>

                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Units Sold</th>
                                <th>Revenue</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topProducts.length > 0 ? (
                                topProducts.map(product => (
                                    <tr key={product.id}>
                                        <td>{getLocalizedProductName(product, 'en')}</td>
                                        <td>{product.sales}</td>
                                        <td>{storeCurrency} {product.revenue?.toFixed(2)}</td>
                                        <td>
                                            <Link to={`/admin/products/edit/${product.id}`} className="admin-action-btn">Edit</Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="no-data">No product data available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="admin-dashboard-section">
                <div className="admin-section-header">
                    <h2>Quick Actions</h2>
                </div>

                <div className="admin-quick-actions">
                    <div
                        onClick={handleAddProduct}
                        className="admin-quick-action-card"
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="admin-quick-action-icon">➕</div>
                        <h3>Add New Product</h3>
                    </div>

                    <Link to="/admin/orders?status=Pending" className="admin-quick-action-card">
                        <div className="admin-quick-action-icon">📋</div>
                        <h3>Process Orders</h3>
                    </Link>

                    <Link to="/admin/inventory" className="admin-quick-action-card">
                        <div className="admin-quick-action-icon">🔄</div>
                        <h3>Update Inventory</h3>
                    </Link>


                    <Link to="/admin/settings" className="admin-quick-action-card">
                        <div className="admin-quick-action-icon">⚙️</div>
                        <h3>Store Settings</h3>
                    </Link>
                </div>
            </div>
        </div>


    );
};

export default AdminDashboard;